import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Mail, Building } from 'lucide-react';

const WarrantyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>SELF Phone Limited Warranty Policy - SELF Labs</title>
        <meta name="description" content="Official Limited Warranty Policy for the SELF Phone, effective April 1, 2025. Learn about warranty coverage, claims process, and support." />
      </Helmet>

      {/* Background Effects */}
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_center,rgba(138,43,226,0.1)_0%,transparent_100%)]" />
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_center,rgba(138,43,226,0.03)_0%,transparent_1px)] bg-[length:24px_24px]" />

      <div className="relative">
        <div className="container mx-auto px-4 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-purple-400 to-blue-400">
              SELF Phone Limited Warranty Policy
            </h1>
            <p className="text-indigo-400 mb-8">Effective Date: April 1, 2025</p>

            {/* Scope of Warranty */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4 text-indigo-400">1. Scope of Warranty</h2>
              <div className="prose prose-invert max-w-none">
                <p>This Limited Warranty ("Warranty") applies to the physical hardware components of the SELF Phone ("Product") manufactured or supplied by SELF Labs Inc. ("SELF Labs," "we," "us," or "our").</p>
                <p>Software services (such as Harmony Signal Engine™, Role Model AI™, Aura Shield™, and Harmony Mesh Participation) are covered separately under the SELF Labs Terms and Conditions and are provided "as-is" without uptime or feature guarantees.</p>
                <p>This Warranty is extended solely to the original purchaser ("you," "your," or "user") of the Product and is non-transferable.</p>
              </div>
            </section>

            {/* Warranty Coverage */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4 text-indigo-400">2. Warranty Coverage</h2>
              <div className="prose prose-invert max-w-none">
                <p>SELF Labs warrants that under normal use and service, the SELF Phone will be free from defects in:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Materials</li>
                  <li>Workmanship</li>
                  <li>Manufacturing processes</li>
                </ul>
                <p>for a period of 12 months (365 days) from the date of delivery ("Warranty Period").</p>
              </div>
            </section>

            {/* What Is Covered */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4 text-indigo-400">3. What Is Covered</h2>
              <div className="prose prose-invert max-w-none">
                <p>During the Warranty Period, SELF Labs will, at its discretion:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Repair the defective hardware component(s)</li>
                  <li>Replace the defective hardware with a new or refurbished unit</li>
                  <li>Issue a credit or refund if repair or replacement is not commercially reasonable</li>
                </ul>
                <p>Warranty service is available for:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Non-functioning or defective screens</li>
                  <li>Battery failures not due to misuse</li>
                  <li>Internal processor or system failures</li>
                  <li>Factory assembly defects affecting device functionality</li>
                </ul>
              </div>
            </section>

            {/* What Is NOT Covered */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4 text-indigo-400">4. What Is NOT Covered</h2>
              <div className="prose prose-invert max-w-none">
                <p>This Warranty does not cover:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Normal wear and tear (scratches, dents, cosmetic damage)</li>
                  <li>Software malfunctions, feature changes, or service interruptions</li>
                  <li>Damage caused by accidents, abuse, misuse, liquid exposure, unauthorized modifications, or repairs by non-authorized service providers</li>
                </ul>
                <p>Issues arising from:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Rooting or jailbreaking the device</li>
                  <li>Unauthorized flashing of custom firmware</li>
                  <li>Alterations to SELF OS, Harmony Signal Engine™, or Role Model AI™ systems</li>
                  <li>Use of the SELF Phone with incompatible accessories or peripherals</li>
                  <li>Damage resulting from failure to follow the Product's usage instructions</li>
                  <li>Loss or theft of the device</li>
                  <li>Participation in unauthorized resale or redistribution activities</li>
                </ul>
              </div>
            </section>

            {/* How to Submit a Warranty Claim */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4 text-indigo-400">5. How to Submit a Warranty Claim</h2>
              <div className="prose prose-invert max-w-none">
                <p>To initiate a warranty claim:</p>
                <p>Contact SELF Labs Support at 📩 data@rolemodel.ai with:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Your original proof of purchase</li>
                  <li>Device serial number</li>
                  <li>Description of the issue</li>
                </ul>
                <p>Follow instructions provided by the SELF Labs Support Team, which may include:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Diagnostic steps</li>
                  <li>Shipping the defective device for evaluation</li>
                </ul>
                <p>Warranty repairs or replacements will be processed after defect confirmation.</p>
                <p>You are responsible for securely packaging the device for shipment. SELF Labs is not liable for damage occurring during return shipping if not properly packed.</p>
              </div>
            </section>

            {/* Warranty Limitations */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4 text-indigo-400">6. Warranty Limitations</h2>
              <div className="prose prose-invert max-w-none">
                <p>Repair or replacement does not restart or extend the original Warranty Period.</p>
                <p>Replacement devices may be new or refurbished.</p>
                <p>Any repaired or replaced devices continue under the original warranty expiration timeline.</p>
                <p>SELF Labs reserves the right to deny warranty service if:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Device tampering is detected</li>
                  <li>Serial numbers have been altered, defaced, or removed</li>
                  <li>Warranty claims are found to be fraudulent</li>
                </ul>
              </div>
            </section>

            {/* Limitation of Liability */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4 text-indigo-400">7. Limitation of Liability</h2>
              <div className="prose prose-invert max-w-none">
                <p>To the fullest extent permitted by law, SELF Labs' liability is limited exclusively to repair, replacement, or credit as provided above.</p>
                <p>SELF Labs shall not be liable for:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Loss of data</li>
                  <li>Indirect, incidental, consequential, punitive, or special damages</li>
                  <li>Downtime, loss of use, or lost opportunities associated with warranty processing</li>
                </ul>
              </div>
            </section>

            {/* Additional Terms */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4 text-indigo-400">8. Additional Terms</h2>
              <div className="prose prose-invert max-w-none">
                <p>This Warranty Policy is incorporated into and governed by the SELF Labs Terms and Conditions.</p>
                <p>Your purchase and use of the SELF Phone are additionally subject to the Harmony Mesh Participant License, the ESSENCE Token Disclaimer, and the Aura Shield Privacy Framework.</p>
              </div>
            </section>

            {/* Contact Information */}
            <section className="mb-12">
              <h2 className="text-2xl font-semibold mb-4 text-indigo-400">9. Contact Information</h2>
              <div className="prose prose-invert max-w-none">
                <p>For warranty support or inquiries, contact:</p>
                <div className="flex items-center space-x-2 mt-4">
                  <Mail className="w-5 h-5 text-indigo-400" />
                  <a href="mailto:data@rolemodel.ai" className="text-indigo-400 hover:text-indigo-300 transition-colors">
                    data@rolemodel.ai
                  </a>
                </div>
                <div className="flex items-center space-x-2 mt-2">
                  <Building className="w-5 h-5 text-indigo-400" />
                  <span>1209 N Orange St, Wilmington, Delaware</span>
                </div>
              </div>
            </section>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default WarrantyPolicyPage; 